import {
    LOGIN_USER,
    LOGOUT_USER,
    REGISTER_USER,
    SEND_SUPPORT_TICKET,
    FORGOT_PASSWORD,
    RESET_PASSWORD,
    UPDATE_USER,
    UPDATE_USER_SUBSCRIPTIONS,
    EAGER_FETCH_PRODUCTS,
    DELETE_USER_ACCOUNT, EXPIRED_PASSWORD
} from "./actions.type";
import {
    REFRESH_USER,
    REMOVE_USER,
    NO_RESPONSE_ERROR,
    ASYNC_ACTION_START,
    ASYNC_ACTION_END,
} from "./mutations.type";
import store from "../store/index";
import {SupportService, UserService} from "@/common/api.service";
import {STATUS, asyncState, asyncGetters, asyncActions, asyncMutations, toMutationPayload} from "./utils/async";
import {resetState} from "./utils/state"
import Utils from "../common/utils";

const adminRoles = ["my-ampleon-superuser", "my-ampleon-marketing-admin", "my-ampleon-sales-admin"];

export function getInitialState() {
    return {
        user: {
            email: "",
            firstName: "",
            lastName: "",
            roles: [],
            groups: []
        },
        isAuthenticated: false,
        ...asyncState(LOGIN_USER, UPDATE_USER, UPDATE_USER_SUBSCRIPTIONS, REGISTER_USER, SEND_SUPPORT_TICKET, FORGOT_PASSWORD, RESET_PASSWORD)
    }
}

const state = getInitialState();

const getters = {
    user(state) {
        return state.user;
    },
    isAuthenticated(state) {
        return state.isAuthenticated;
    },
    isAdmin(state) {
        return state.user.roles && state.user.roles.some(role => adminRoles.includes(role))
    },
    isAverageUser(state) {
        return state.user.roles && state.user.roles.includes('average-user');
    },
    isSalesAdmin(state) {
        return state.user.roles && state.user.roles.includes("my-ampleon-sales-admin");
    },
    isMarketingAdmin(state) {
        return state.user.roles && state.user.roles.includes("my-ampleon-marketing-admin");
    },
    isMyAmpleonSuperuser(state) {
        return state.user.roles && state.user.roles.includes("my-ampleon-superuser");
    },
    ...asyncGetters()
};

const actions = {
    [LOGIN_USER]({commit}, params) {
        commit(ASYNC_ACTION_START, LOGIN_USER);

        let operation = {action: LOGIN_USER, successCode: 200, mutation: loginUserCallback};

        let payload = {
            username: params.email,
            password: params.password,
        };
        return UserService.loginUser(payload)
            .then(response => {
                if (response.status === 302) {
                    return response.data;
                }

                store.dispatch('parametricSearch/' + EAGER_FETCH_PRODUCTS, null).then(() => {
                    commit(ASYNC_ACTION_END, toMutationPayload(operation, response));
                    Utils.tackUserId(response.data.userId);
                    console.log("parametric table data eagerly loaded.");
                }).catch(() => {
                    console.warn("cannot eagerly load parametric table.");
                    commit(ASYNC_ACTION_END, toMutationPayload(operation, response));
                    Utils.tackUserId(response.data.userId);
                });
            })
            .catch(error => {
                commit(NO_RESPONSE_ERROR, toMutationPayload(operation, error.response));
                throw new Error(error);
            });
    },
    [UPDATE_USER]({commit}, params) {
        commit(ASYNC_ACTION_START, UPDATE_USER);

        let operation = {action: UPDATE_USER, successCode: 200};

        let payload = {
            firstName: params.firstName,
            lastName: params.lastName,
            password: params.password,
            companyName: params.company,
            jobDescription: params.jobDescription,
            businessTitle: params.businessTitle,
            phone: params.phone,
        };

        return UserService.updateUserAccount(payload, params.email)
            .then(response => {
                commit(ASYNC_ACTION_END, toMutationPayload(operation, response));
            })
            .catch(error => {
                commit(NO_RESPONSE_ERROR, toMutationPayload(operation, error.response));
                throw new Error(error);
            });
    },
    [UPDATE_USER_SUBSCRIPTIONS]({commit}, params) {
        commit(ASYNC_ACTION_START, UPDATE_USER);

        let operation = {action: UPDATE_USER_SUBSCRIPTIONS, successCode: 200};

        let payload = {
            subscriptionFrequencies: params.subscriptionFrequencies,
            subscriptionTopics: params.subscriptionTopics
        };

        return UserService.updateUserSubscriptions(payload, params.email)
            .then(response => {
                commit(ASYNC_ACTION_END, toMutationPayload(operation, response));
            })
            .catch(error => {
                commit(NO_RESPONSE_ERROR, toMutationPayload(operation, error.response));
                throw new Error(error);
            });
    },
    [LOGOUT_USER]({commit}) {
        commit(LOGOUT_USER);
    },
    [REGISTER_USER]({commit}, params) {
        commit(ASYNC_ACTION_START, REGISTER_USER);

        let operation = {action: REGISTER_USER, successCode: 201, errors: {409: STATUS.EMAIL_EXISTS}};

        let payload = {
            firstName: params.firstName,
            lastName: params.lastName,
            username: params.email,
            password: params.password,
            companyName: params.company,
            jobDescription: params.job,
            businessTitle: params.business,
            phone: params.phone,
            country: params.country,
            token: params.token,
            subscribed: params.subscribed
        };
        return UserService.registerUser(payload)
            .then(response => {
                commit(ASYNC_ACTION_END, toMutationPayload(operation, response));
            })
            .catch(error => {
                commit(NO_RESPONSE_ERROR, toMutationPayload(operation, error.response));
                throw new Error(error);
            });
    },
    [SEND_SUPPORT_TICKET]({commit}, params) {
        commit(ASYNC_ACTION_START, SEND_SUPPORT_TICKET);

        let operation = {action: SEND_SUPPORT_TICKET, successCode: 200};

        let payload = {
            firstName: params.firstName,
            lastName: params.lastName,
            email: params.email,
            textArea: params.textArea,
            token: params.token,
        };
        return SupportService.registrationLoginSupport(payload, params.files)
            .then(response => {
                commit(ASYNC_ACTION_END, toMutationPayload(operation, response));
            })
            .catch(error => {
                commit(NO_RESPONSE_ERROR, toMutationPayload(operation, error.response));
                throw new Error(error);
            });
    },
    [FORGOT_PASSWORD]({commit}, params) {
        commit(ASYNC_ACTION_START, FORGOT_PASSWORD);

        let operation = {action: FORGOT_PASSWORD, successCode: 200};

        return UserService.forgotPassword(params)
            .then(response => {
                commit(ASYNC_ACTION_END, toMutationPayload(operation, response));
            })
            .catch(error => {
                commit(NO_RESPONSE_ERROR, toMutationPayload(operation, error.response));
                throw new Error(error);
            });
    },
    [RESET_PASSWORD]({commit}, params) {
        commit(ASYNC_ACTION_START, RESET_PASSWORD);

        let operation = {action: RESET_PASSWORD, successCode: 200};

        let payload = {
            email: params.email,
            password: params.password,
            token: params.token
        };
        return UserService.resetPassword(payload)
            .then(response => {
                commit(ASYNC_ACTION_END, toMutationPayload(operation, response));
            })
            .catch(error => {
                commit(NO_RESPONSE_ERROR, toMutationPayload(operation, error.response));
                throw new Error(error);
            });
    },
    [EXPIRED_PASSWORD]({commit}, params) {
        commit(ASYNC_ACTION_START, EXPIRED_PASSWORD);

        let operation = {action: EXPIRED_PASSWORD, successCode: 200};

        let payload = {
            email: params.email,
            oldPassword: params.oldPassword,
            password: params.password,
            confirmPassword: params.confirmPassword,
            token: params.token
        };

        return UserService.resetExpiredPassword(payload)
            .then(response => {
                console.log(`response: ${JSON.stringify(response)}`);
                commit(ASYNC_ACTION_END, toMutationPayload(operation, response));
            })
            .catch(error => {
                commit(NO_RESPONSE_ERROR, toMutationPayload(operation, error.response));
                throw new Error(error);
            });
    },
    [DELETE_USER_ACCOUNT]({commit}, params) {
        return UserService.deleteUserAccount(params)
            .then(response => {
                commit(REMOVE_USER, response);
            })
            .catch(error => {
                throw new Error(error);
            });

    },
    ...asyncActions()
};

const mutations = {
    [LOGOUT_USER]() {
        resetState();
    },
    [REFRESH_USER](state, response) {
        loginUserCallback(state, response)
    },
    [REMOVE_USER](state, response) {
        if (response.status === 200) {
            state.user = {
                email: "",
                firstName: "",
                lastName: "",
                roles: [],
                groups: []
            }
        }
    },
    ...asyncMutations()
};

function loginUserCallback(state, response) {
    state.user = response.data;
    state.isAuthenticated = true;
}

export default {
    state,
    getters,
    actions,
    mutations
}
